import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    IconButton,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Grid,
    Chip,
    CardContent,
    Card,
    Divider,
    Button,
    TextField,
    InputBase
} from "@mui/material";
import { styled, alpha } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import t from "../translations/generalInfrastructureTranslation";
import { isTestEnv } from "../globals";

const ACCENT = "#1976d2";

const cardSx = {
    height: '100%',
    borderRadius: 4,
    overflow: 'hidden',
    border: `1px solid ${alpha('#000000', 0.06)}`,
    background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
};

const EmptyChip = styled(InputBase)(({ theme }) => ({
    '& .MuiInputBase-input': {
        border: `1px dashed ${theme.palette.primary.main}`,
        borderRadius: '16px',
        padding: '4px 10px',
        fontSize: '0.8125rem',
        fontWeight: '400',
        lineHeight: '1.5',
        height: '24px',
        width: '32px',
        '&:focus': {
            border: `1px solid ${theme.palette.primary.main}`,
            outline: 'none',
        },
    },
}));

const GeneralInfrastructure = ({ setMockDropdownData, execute }) => {
    const [newRow, setNewRow] = useState({ code: '', des: '' });
    const [isAdding, setIsAdding] = useState(false);
    const [contactCodes, setContactCodes] = useState([]);
    const [newInternalType, setNewInternalType] = useState('');
    const [internalOrderTypes, setInternalOrderTypes] = useState([]);
    const [newExternalType, setNewExternalType] = useState('');
    const [externalOrderTypes, setExternalOrderTypes] = useState([]);

    useEffect(() => {
        const getData = async () => {
            try {
                const externalOrderTypesResponse = await execute(
                    "GET",
                    `${process.env.REACT_APP_BACKEND_BASE_URL}/api/generalData/externalOrderTypes`
                );
                setExternalOrderTypes(externalOrderTypesResponse);

                const internalOrderTypesResponse = await execute(
                    "GET",
                    `${process.env.REACT_APP_BACKEND_BASE_URL}/api/generalData/internalOrderTypes`
                );
                setInternalOrderTypes(internalOrderTypesResponse);

                const contactCodesResponse = await execute(
                    "GET",
                    `${process.env.REACT_APP_BACKEND_BASE_URL}/api/generalData/contactCodes`
                );
                setContactCodes(contactCodesResponse);

            } catch (error) {
                isTestEnv && console.error(error);
            }
        };
        getData();
    }, [execute]);

    const handleSaveContactCode = async () => {
        try {
            const response = await execute(
                "POST",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/generalData/contactCode`,
                { code: newRow.code, des: newRow.des }
            );
            setContactCodes(response);
            setNewRow({ code: '', des: '' });
            setIsAdding(false);
        } catch (err) {
            isTestEnv && console.error(err);
        }
    };

    const handleAddContactCode = () => {
        setIsAdding(true);
    };

    const handleDeleteContactCode = async (id) => {
        try {
            await execute(
                "DELETE",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/generalData/contactCode/${id}`
            );
            setContactCodes(contactCodes.filter(c => c._id !== id));
        } catch (err) {
            isTestEnv && console.error(err);
        }
    };

    const handleAddnewInternalType = async (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (newInternalType.trim() !== '') {
                try {
                    const resp = await execute("POST", `${process.env.REACT_APP_BACKEND_BASE_URL}/api/generalData/internalOrderType`, { name: newInternalType.trim() });
                    setInternalOrderTypes(resp);
                    setMockDropdownData((prev) => ({ ...prev, intOrderType: resp }));
                } catch (err) {
                    isTestEnv && console.error(err);
                }
                setNewInternalType('');
            }
        }
    };

    const handleDeleteInternalOrder = async (id) => {
        try {
            await execute(
                "DELETE",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/generalData/internalOrderType/${id}`
            );
            const updatedTypes = internalOrderTypes.filter(e => e._id !== id);
            setInternalOrderTypes(updatedTypes);
            setMockDropdownData((prev) => ({ ...prev, intOrderType: updatedTypes }));
        } catch (err) {
            isTestEnv && console.error(err);
        }
    };

    const handleAddnewExternalType = async (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (newExternalType.trim() !== '') {
                try {
                    const resp = await execute("POST", `${process.env.REACT_APP_BACKEND_BASE_URL}/api/generalData/externalOrderType`, { description: newExternalType.trim() });
                    setExternalOrderTypes(resp);
                    setMockDropdownData((prev) => ({ ...prev, extOrderType: resp }));
                } catch (err) {
                    isTestEnv && console.error(err);
                }
                setNewExternalType('');
            }
        }
    };

    const handleDeleteExternalOrder = async (id) => {
        try {
            await execute(
                "DELETE",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/generalData/externalOrderType/${id}`
            );
            const updatedTypes = externalOrderTypes.filter(e => e._id !== id);
            setExternalOrderTypes(updatedTypes);
            setMockDropdownData((prev) => ({ ...prev, extOrderType: updatedTypes }));
        } catch (err) {
            isTestEnv && console.error(err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewRow({
            ...newRow,
            [name]: value,
        });
    };

    return (
        <Box mt={2}>
            <Typography variant="h5" gutterBottom>{t.promotionSamples}</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={cardSx}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: ACCENT, fontWeight: 700 }}>{t.externalOrderTypes}</Typography>
                            {externalOrderTypes.map((item) => (
                                <Chip
                                    key={item._id}
                                    label={item.description}
                                    onDelete={() => handleDeleteExternalOrder(item._id)}
                                    variant="outlined"
                                    sx={{ m: 0.5, borderColor: alpha(ACCENT, 0.4), color: ACCENT }}
                                />
                            ))}
                            <EmptyChip
                                value={newExternalType}
                                onChange={(e) => setNewExternalType(e.target.value)}
                                onKeyDown={handleAddnewExternalType}
                                placeholder="+"
                                sx={{ m: 0.5 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={cardSx}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: ACCENT, fontWeight: 700 }}>{t.internalOrderTypes}</Typography>
                            {internalOrderTypes.map((item) => (
                                <Chip
                                    key={item._id}
                                    label={item.name}
                                    onDelete={() => handleDeleteInternalOrder(item._id)}
                                    variant="outlined"
                                    sx={{ m: 0.5, borderColor: alpha(ACCENT, 0.4), color: ACCENT }}
                                />
                            ))}
                            <EmptyChip
                                value={newInternalType}
                                onChange={(e) => setNewInternalType(e.target.value)}
                                onKeyDown={handleAddnewInternalType}
                                placeholder="+"
                                sx={{ m: 0.5 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={cardSx}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: ACCENT, fontWeight: 700 }}>{t.contactCodes}</Typography>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t.code}</TableCell>
                                            <TableCell>{t.description}</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {contactCodes.map((row) => (
                                            <TableRow key={row._id}>
                                                <TableCell>{row.code}</TableCell>
                                                <TableCell>{row.des}</TableCell>
                                                <TableCell>
                                                    <IconButton onClick={() => handleDeleteContactCode(row._id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {isAdding && (
                                            <TableRow>
                                                <TableCell>
                                                    <TextField
                                                        name="code"
                                                        value={newRow.code}
                                                        onChange={handleInputChange}
                                                        fullWidth
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        name="des"
                                                        value={newRow.des}
                                                        onChange={handleInputChange}
                                                        fullWidth
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Button onClick={handleSaveContactCode}>{t.save}</Button>
                                                    <Button onClick={() => setIsAdding(false)}>{t.cancel}</Button>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Divider />
                            <Button
                                startIcon={<AddIcon />}
                                onClick={handleAddContactCode}
                                sx={{ mt: 2, color: ACCENT }}
                            >
                                {t.addContactCode}
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default GeneralInfrastructure;
